import type { RenewalReason } from "./schemas/home.ts";
import { formatSessionDay, shiftDateKey } from "./time.ts";

/**
 * The Renewals list on owner Home (Studio Home H3), where clients book for themselves. It replaces
 * Running out there. Pure, so the API, Desk web and the phone read one rule and one set of words.
 *
 * A client is on the list when the newest package (by the day it was bought) runs out or ends:
 * 2 sessions left or fewer, or the package ends in 10 days or fewer. A newer package takes the
 * client off the list, because then the newest package is the new one. A paused package (M3) takes
 * the client off the list too: the pause moves the end date, and the client is away.
 */

/** Sessions left on the account at or under this: on the list. */
export const RENEWAL_AT_SESSIONS = 2;
/** The newest package ends in this many days or fewer: on the list. */
export const RENEWAL_ENDS_IN_DAYS = 10;
/**
 * A newest package that ended more than this many days ago is not on the list: that client left,
 * and the Quiet filter of the Clients page has them.
 */
export const RENEWAL_FOLLOW_UP_DAYS = 30;

export type RenewalFacts = {
  /** Today in the business timezone, `yyyy-MM-dd`. */
  today: string;
  /** Sessions left on the account, every current package together. */
  sessionsLeft: number;
  /** The package bought last (refunded ones left out), or null when the client never had one. */
  newest: { intro: boolean; endsOn: string } | null;
  /** A package of the account is paused now, or a pause is set to start. */
  paused: boolean;
};

/** Why the client is on the Renewals list, or null when the client is not on it. */
export function renewalReason(facts: RenewalFacts): RenewalReason | null {
  const { newest, today } = facts;
  if (!newest || facts.paused) return null;
  if (newest.endsOn < shiftDateKey(today, -RENEWAL_FOLLOW_UP_DAYS)) return null;
  const low = facts.sessionsLeft <= RENEWAL_AT_SESSIONS;
  const endsSoon = newest.endsOn <= shiftDateKey(today, RENEWAL_ENDS_IN_DAYS);
  if (!low && !endsSoon) return null;
  if (newest.intro) return "intro_not_converted";
  return low ? "low_sessions" : "ends_soon";
}

/** The group titles of the list, one for each reason, in the order the screens show them. */
export const renewalReasonTitles: Record<RenewalReason, string> = {
  low_sessions: "Few sessions left",
  ends_soon: "Package ends soon",
  intro_not_converted: "Intro offer, no package yet",
};

/** "Fri 30 Oct" for a `yyyy-MM-dd` key. Calendar days have no time zone. */
export function renewalDayText(key: string): string {
  return formatSessionDay(new Date(`${key}T12:00:00.000Z`), "UTC");
}

/** The short line under a client's name: "1 session left", "Ends Fri 30 Oct", "Intro used". */
export function renewalRowText(row: { reason: RenewalReason; endsOn: string; sessionsLeft: number }, today: string): string {
  const left = row.sessionsLeft === 0 ? "No sessions left" : `${row.sessionsLeft} ${row.sessionsLeft === 1 ? "session" : "sessions"} left`;
  const ended = row.endsOn < today;
  switch (row.reason) {
    case "low_sessions":
      return ended ? `${left}. Ended ${renewalDayText(row.endsOn)}` : left;
    case "ends_soon":
      return `${left}. Ends ${renewalDayText(row.endsOn)}`;
    case "intro_not_converted":
      return row.sessionsLeft === 0 ? `Intro offer used${ended ? `, ended ${renewalDayText(row.endsOn)}` : ""}` : `Intro offer, ${left.toLowerCase()}`;
  }
}
